// src/Dashboard/pages/teacher/PrepaPhysique/PrepaPhysiqueDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { usePrepaPhysiqueDashboard } from "./hooks/usePrepaPhysiqueDashboard";

import PrepaPhysiqueHeader from "./components/PrepaPhysiqueHeader";
import PrepaPhysiqueTabs from "./components/PrepaPhysiqueTabs";
import PrepaPhysiqueKpiCards from "./components/PrepaPhysiqueKpiCards";

import ExerciseFiltersCard from "./components/ExerciseFiltersCard";
import ExerciseDataTable from "./components/ExerciseDataTable";
import ExerciseDrawer from "./components/ExerciseDrawer";

import AssignmentFiltersCard from "./components/AssignmentFiltersCard";
import AssignmentDataTable from "./components/AssignmentDataTable";
import AssignmentCreateDrawer from "./components/AssignmentCreateDrawer";
import AssignmentEditDrawer from "./components/AssignmentEditDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0,
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Préparation physique
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PrepaPhysiqueDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const authorized = roleId === 1 || roleId === 3;
  const isAdmin = roleId === 1;
  const isTeacher = roleId === 3;

  const {
    activeTab,
    setActiveTab,

    exercises,
    filteredExercises,
    exerciseFilters,
    setExerciseFilters,
    exerciseProgrammes,

    assignments,
    filteredAssignments,
    assignmentFilters,
    setAssignmentFilters,
    assignmentProgrammes,

    adherents,
    groups,
    seasons,

    stats,
    loading,
    saving,
    deletingId,

    exerciseDrawerOpen,
    exerciseDrawerMode,
    currentExercise,
    openCreateExerciseDrawer,
    openEditExerciseDrawer,
    openExerciseDetailsDrawer,
    closeExerciseDrawer,

    assignmentCreateDrawerOpen,
    assignmentPresetExercise,
    openCreateAssignmentDrawer,
    closeCreateAssignmentDrawer,

    assignmentEditDrawerOpen,
    currentAssignment,
    openEditAssignmentDrawer,
    closeEditAssignmentDrawer,

    handleCreateExercise,
    handleUpdateExercise,
    handleDeleteExercise,

    handleCreateAssignments,
    handleUpdateAssignment,
    handleDeleteAssignment,

    refresh,
  } = usePrepaPhysiqueDashboard();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <PrepaPhysiqueHeader
          loading={loading}
          onRefresh={refresh}
          onCreateExercise={openCreateExerciseDrawer}
          onCreateAssignment={() => openCreateAssignmentDrawer(null)}
          isAdmin={isAdmin}
          isTeacher={isTeacher}
        />

        <PrepaPhysiqueTabs activeTab={activeTab} onChange={setActiveTab} />

        <PrepaPhysiqueKpiCards stats={stats} loading={loading} />

        {activeTab === "exercices" ? (
          <>
            <ExerciseFiltersCard
              filters={exerciseFilters}
              setFilters={setExerciseFilters}
              programmes={exerciseProgrammes}
              loading={loading}
              onAdd={openCreateExerciseDrawer}
            />

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Bibliothèque d’exercices
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Exercices de préparation physique
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Gérez les exercices, les programmes, les descriptions,
                      images et vidéos. Vous pouvez ensuite affecter un exercice
                      à un ou plusieurs adhérents.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                    <span className="text-slate-500 dark:text-slate-400">
                      Résultats :
                    </span>{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredExercises.length}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {" "}
                      / {exercises.length}
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  <ExerciseDataTable
                    exercises={filteredExercises}
                    loading={loading}
                    deletingId={deletingId}
                    onView={openExerciseDetailsDrawer}
                    onEdit={openEditExerciseDrawer}
                    onDelete={handleDeleteExercise}
                    onAssign={openCreateAssignmentDrawer}
                  />
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <AssignmentFiltersCard
              filters={assignmentFilters}
              setFilters={setAssignmentFilters}
              programmes={assignmentProgrammes}
              loading={loading}
              onAdd={() => openCreateAssignmentDrawer(null)}
            />

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Affectations
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Suivi des exercices assignés
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Affectez des exercices par lot selon la saison, le groupe,
                      la catégorie d’âge ou l’année de naissance, puis suivez
                      les exercices réalisés.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                    <span className="text-slate-500 dark:text-slate-400">
                      Résultats :
                    </span>{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredAssignments.length}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {" "}
                      / {assignments.length}
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  <AssignmentDataTable
                    assignments={filteredAssignments}
                    loading={loading}
                    deletingId={deletingId}
                    onEdit={openEditAssignmentDrawer}
                    onDelete={handleDeleteAssignment}
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </div>

      <ExerciseDrawer
        isOpen={exerciseDrawerOpen}
        mode={exerciseDrawerMode}
        exercise={currentExercise}
        saving={saving}
        onClose={closeExerciseDrawer}
        onCreate={handleCreateExercise}
        onUpdate={handleUpdateExercise}
      />

      <AssignmentCreateDrawer
        isOpen={assignmentCreateDrawerOpen}
        presetExercise={assignmentPresetExercise}
        exercises={exercises}
        adherents={adherents}
        groups={groups}
        seasons={seasons}
        existingAssignments={assignments}
        saving={saving}
        onClose={closeCreateAssignmentDrawer}
        onSubmit={handleCreateAssignments}
      />

      <AssignmentEditDrawer
        isOpen={assignmentEditDrawerOpen}
        assignment={currentAssignment}
        saving={saving}
        onClose={closeEditAssignmentDrawer}
        onSubmit={handleUpdateAssignment}
      />

      <ToastContainer />
    </section>
  );
}
